import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {ExternalServiceType} from 'nutshell-graphql-types';

import {useSetAircallIntegrationIsEnabledForUser} from '../../../setup/phone/graphql/hooks/use-set-aircall-integration-is-enabled-for-user';
import SET_DIALPAD_INTEGRATION_IS_ENABLED from '../../../setup/phone/graphql/mutations/set-dialpad-integration-is-enabled.graphql';
import SET_KIXIE_INTEGRATION_IS_ENABLED from '../../../setup/phone/graphql/mutations/set-kixie-integration-is-enabled.graphql';
import SET_RINGCENTRAL_INTEGRATION_IS_ENABLED from '../../../setup/phone/graphql/mutations/set-ringcentral-integration-is-enabled.graphql';

type SetIsEnabledMutation = (arg1: boolean) => Promise<any>;

export const useSetPhoneIntegrationIsEnabled = (): {
    getSetIsEnabledMutationByIntegration: (
        arg1: ExternalServiceType
    ) => (isEnabled: boolean) => Promise<any> | null | undefined;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const {
        setAircallIntegrationIsEnabledForUser: setAircallEnabled,
        isLoading: loadingAircall,
        error: errorAircall,
    } = useSetAircallIntegrationIsEnabledForUser();

    const [setDialpadEnabled, {loading: loadingDialpad, error: errorDialpad}] = useMutation(
        SET_DIALPAD_INTEGRATION_IS_ENABLED
    );

    const [setRingCentralEnabled, {loading: loadingRingCentral, error: errorRingCentral}] =
        useMutation(SET_RINGCENTRAL_INTEGRATION_IS_ENABLED);

    const [setKixieEnabled, {loading: loadingKixie, error: errorKixie}] = useMutation(
        SET_KIXIE_INTEGRATION_IS_ENABLED
    );

    const mutationMap = {
        AIRCALL: (isEnabled: boolean) => setAircallEnabled(isEnabled),
        DIALPAD: (isEnabled: boolean) => setDialpadEnabled({variables: {isEnabled}}),
        RINGCENTRAL: (isEnabled: boolean) => setRingCentralEnabled({variables: {isEnabled}}),
        KIXIE: (isEnabled: boolean) => setKixieEnabled({variables: {isEnabled}}),
    } as const;

    const getSetIsEnabledMutationByIntegration = (
        integrationType: ExternalServiceType
    ): SetIsEnabledMutation | null | undefined => {
        if (
            integrationType !== 'AIRCALL' &&
            integrationType !== 'DIALPAD' &&
            integrationType !== 'RINGCENTRAL' &&
            integrationType !== 'KIXIE'
        ) {
            return () => Promise.resolve();
        }

        return mutationMap[integrationType];
    };

    return {
        // @ts-expect-error
        getSetIsEnabledMutationByIntegration,
        isLoading: loadingAircall || loadingDialpad || loadingRingCentral || loadingKixie,
        error:
            errorAircall ||
            // @ts-expect-error
            safelyGetGraphQLErrorMessage(errorDialpad) ||
            // @ts-expect-error
            safelyGetGraphQLErrorMessage(errorRingCentral) ||
            // @ts-expect-error
            safelyGetGraphQLErrorMessage(errorKixie),
    };
};
